import { getLogger } from '@logtape/logtape';
import { Button } from '@mantine/core';
import { FC, useMemo, useState } from 'react';
import { mutate } from 'swr';

import useConfig from '@/hooks/useConfig.ts';

import { GenericPatient, PhoneNumberType } from '@/models/GenericPatient.ts';
import { NotificationType } from '@/models/Notification.ts';
import { EditPatientFormData, PatientFormData, UpdatePatient } from '@/models/Patient.ts';
import { InsuranceType } from '@/models/insurance.ts';

import {
    showLoadingNotification,
    showWarningNotification,
    updateNotification,
} from '@/services/notificationService.ts';
import { updateUser } from '@/services/patientService.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';
import { hasAddressParts, triggerFormSubmit, validatePatientForm } from '@/utils/formUtils.ts';
import { getPreferredPhoneNumberByType, transformFormDataToUpdatePatient } from '@/utils/patientUtils.ts';

import BaseModal from '../BaseModal.tsx';
import { PatientDataForm } from '../form/patient-data-form.tsx';

const logger = getLogger(['component', 'patient-information-edit-modal']);

// Type for patient edit data
interface Props {
    patient: GenericPatient;
    opened: boolean;
    onClose: () => void;
}

const PatientInformationEditModal: FC<Props> = ({ patient, opened, onClose }) => {
    const [loading, setLoading] = useState(false);
    const { getInstanceConfig } = useConfig();

    const initial_values = useMemo<EditPatientFormData>(() => {
        let default_email: string | undefined = undefined;

        if (patient.contact.emails.length > 0) {
            default_email =
                patient.contact.emails.find((email) => email.is_preferred)?.value ?? patient.contact.emails[0].value;
        }

        const default_phone: string | undefined = getPreferredPhoneNumberByType(
            patient.contact.phone_numbers,
            PhoneNumberType.MOBILE
        );
        const default_landline_phone: string | undefined = getPreferredPhoneNumberByType(
            patient.contact.phone_numbers,
            PhoneNumberType.LANDLINE
        );

        const values: EditPatientFormData = {
            insurance_type: (patient.insurances?.[0]?.type as InsuranceType) ?? InsuranceType.PUBLIC,
            insurance_name: patient.insurances?.[0]?.name ?? '',
            academic_title: patient.personal.academic_title ?? '',
            salutation: patient.personal.salutation ?? 'NONE',
            gender: patient.personal.gender ?? '',
            first_name: patient.personal.first_name ?? '',
            last_name: patient.personal.last_name ?? '',
            date_of_birth: dayjs(patient.personal.date_of_birth).format('DD.MM.YYYY') ?? '',
            street: patient.address?.street ?? '',
            pvs_approved: patient.is_data_admission_to_pvs_approved ?? false,
            house_number: patient.address?.house_number ?? '',
            addon: patient.address?.addon ?? '',
            postal_code: patient.address?.zip ?? '',
            city: patient.address?.city ?? '',
            email: default_email,
            phone: default_phone,
            landline_phone: default_landline_phone,
            country_code: patient.address?.country_code ?? 'DE',
        };

        return values;
    }, [patient]);

    const handleSubmit = async (values: PatientFormData | EditPatientFormData): Promise<void> => {
        // Since we're in edit mode, we know this is EditPatientFormData
        const edit_values = values as EditPatientFormData; // TODO: fix this

        const { show_notification, message } = validatePatientForm(
            edit_values,
            patient.pvs_provider,
            true,
            hasAddressParts(initial_values)
        );
        if (show_notification) {
            showWarningNotification(message ?? '', 'Fehlende Informationen');
            return;
        }

        const notification_id: string = showLoadingNotification('Patient aktualisieren...');
        const insurance_id: string = patient.insurances[0]?.id ?? '';

        try {
            setLoading(true);

            const config = getInstanceConfig(patient.instance_id);
            if (!config) {
                logger.warn('Instance config not found');
                updateNotification(NotificationType.ERROR, {
                    id: notification_id,
                    message: 'Patient aktualisieren fehlgeschlagen!',
                });
                return;
            }

            const updated_patient: UpdatePatient = transformFormDataToUpdatePatient(edit_values, insurance_id);
            const updated_user: GenericPatient | undefined = await updateUser(
                patient.pvs_user_id,
                patient.instance_id,
                updated_patient,
                config.pvs_provider
            );

            if (!updated_user) {
                logger.warn('Error while updating patient');
                updateNotification(NotificationType.ERROR, {
                    id: notification_id,
                    message: 'Patient aktualisieren fehlgeschlagen!',
                });
                return;
            }

            // Success path
            await mutate({
                key: 'patient',
                instance_id: patient.instance_id,
                user_id: patient.pvs_user_id,
            });
            onClose();
            updateNotification(NotificationType.SUCCESS, {
                id: notification_id,
                message: 'Patient erfolgreich aktualisiert!',
            });
        } catch (error) {
            // Error handling
            logger.warn('Error updating patient', { error: error });
            updateNotification(NotificationType.ERROR, {
                id: notification_id,
                message: 'Patient aktualisieren fehlgeschlagen!',
            });
        }

        setLoading(false);
    };

    const footer_element = (
        <div className="flex w-full justify-between">
            <Button variant="outline" onClick={onClose}>
                Abbrechen
            </Button>
            <Button onClick={triggerFormSubmit} loading={loading}>
                Speichern
            </Button>
        </div>
    );

    return (
        // TODO: move it to modal manager
        <BaseModal
            opened={opened}
            onClose={onClose}
            size="xl"
            title={`${patient.personal.last_name} ${patient.personal.first_name} bearbeiten`}
            footer={footer_element}
            zIndex={201}
        >
            <PatientDataForm
                initialValues={initial_values}
                handleSubmit={handleSubmit}
                isEdit
                pvsProvider={patient.pvs_provider}
            />
        </BaseModal>
    );
};

export default PatientInformationEditModal;
